import * as fs from 'node:fs';
import * as path from 'node:path';
import { cliSimpleGit } from '../../cli-simple-git.js';
import type { WorkingTreeChangeKind } from '../types.js';
import { countLinesInText, countTextFileLines } from '../lines/count-lines.js';

const MAX_BYTES = 512_000;

async function countLinesInGitRef(
  repoGitCwd: string,
  ref: string,
  pathInRepo: string,
): Promise<number | null> {
  const spec = `${ref}:${pathInRepo}`;
  const g = cliSimpleGit(repoGitCwd);
  try {
    await g.raw(['cat-file', '-e', spec]);
  } catch {
    return null;
  }
  try {
    const raw = String(await g.raw(['show', spec]));
    const capped = raw.length > MAX_BYTES ? raw.slice(0, MAX_BYTES) : raw;
    return countLinesInText(capped);
  } catch {
    return null;
  }
}

/** Line count in the post-change file for working-tree rows (disk / HEAD). */
export async function resolveWorkingTreeFileTotalLines(options: {
  repoGitCwd: string;
  pathInRepo: string;
  change: WorkingTreeChangeKind;
  isBinary?: boolean;
}): Promise<number | null> {
  if (options.isBinary) return null;
  const filePath = path.join(options.repoGitCwd, options.pathInRepo);
  if (options.change === 'removed') {
    return countLinesInGitRef(options.repoGitCwd, 'HEAD', options.pathInRepo);
  }
  if (options.change === 'moved' || options.change === 'modified') {
    try {
      const st = await fs.promises.stat(filePath);
      if (!st.isFile()) return null;
      return await countTextFileLines(filePath);
    } catch {
      return null;
    }
  }
  return null;
}

/** Line count in the post-change file for a single commit diff. */
export async function resolveCommitFileTotalLines(options: {
  repoGitCwd: string;
  pathInRepo: string;
  change: WorkingTreeChangeKind;
  commitSha: string;
  parentSha: string;
  isBinary?: boolean;
}): Promise<number | null> {
  if (options.isBinary) return null;
  if (options.change === 'removed') {
    return countLinesInGitRef(options.repoGitCwd, options.parentSha, options.pathInRepo);
  }
  if (options.change === 'moved' || options.change === 'modified') {
    return countLinesInGitRef(options.repoGitCwd, options.commitSha, options.pathInRepo);
  }
  return null;
}
